/* ──────────────────────────────────────────────────────────────
   Informe de AUSENCIAS
   · Pestaña 1 → totales globales
   · Pestaña 2 → ausencias por profesor  +  detalle desplegable
   ────────────────────────────────────────────────────────────── */
import { useState } from "react";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";

/* ── MUI ─────────────────────────────────────────── */
import {
  Tabs,
  Tab,
  Stack,
  TextField,
  Button,
  LinearProgress,
  Typography,
  Alert,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Box,
  IconButton,
  Collapse,
} from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import AttachFileIcon from "@mui/icons-material/AttachFile";

/* ── Recharts ────────────────────────────────────── */
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, Legend, Cell } from "recharts";

/* ── API / hooks ─────────────────────────────────── */
import { useAusenciasTotalesReport, useAusenciasProfesorReport } from "../../../hooks/useReportJob";
import type { AusProfRow, AusDetRow } from "../../../api/reports";
import type { Ausencia } from "../../../hooks/useAusencias";

import api from "../../../api/axios";
import DlgAusenciaEdit from "../../../components/DlgAusenciaEdit";
import { downloadReportFile } from "../../../components/utils/downloadXlsx";

const COLORS = ["#4caf50", "#f44336", "#2196f3"]; // gráfico
const fmt = (iso: string) => dayjs(iso).format("DD-MM-YYYY");

/* ═════════════════════════════════════════════════════
   FILA con “collapse”  (detalle del profesor)
   ═════════════════════════════════════════════════════ */
function Row({ row, onOpenAbs }: { row: AusProfRow; onOpenAbs: (id: number) => void }) {
  const [open, setOpen] = useState(false);
  const { t } = useTranslation<"translation">();

  return (
    <>
      {/* — cabecera profesor — */}
      <TableRow>
        <TableCell padding="checkbox">
          <IconButton size="small" onClick={() => setOpen((o) => !o)}>
            {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </TableCell>

        <TableCell>{row.profesor}</TableCell>
        <TableCell align="right">{row.justN}</TableCell>
        <TableCell align="right">{row.injustN}</TableCell>
        <TableCell align="right">{row.totalN}</TableCell>
        <TableCell>{fmt(row.ultima)}</TableCell>
      </TableRow>

      {/* — detalle ausencias — */}
      <TableRow>
        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={6}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box margin={1}>
              <Typography variant="subtitle2" gutterBottom>
                {t("ausencias.detalle.titulo")}
              </Typography>

              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell />
                    <TableCell>{t("ausencias.detalle.fecha")}</TableCell>
                    <TableCell>{t("ausencias.detalle.tipo")}</TableCell>
                    <TableCell>{t("ausencias.detalle.motivo")}</TableCell>
                    <TableCell>{t("ausencias.detalle.jornada")}</TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {row.detalle.map((d: AusDetRow, i: number) => (
                    <TableRow key={i}>
                      {/* 📎 si hay adjuntos */}
                      <TableCell padding="checkbox">
                        {d.adjN > 0 && (
                          <IconButton
                            size="small"
                            onClick={() => onOpenAbs(d.id)}
                            title={t("ausencias.detalle.verAdjunto") as string}
                          >
                            <AttachFileIcon fontSize="small" />
                          </IconButton>
                        )}
                      </TableCell>

                      <TableCell>{fmt(d.fecha)}</TableCell>
                      <TableCell>
                        {d.tipo === "justificada"
                          ? t("ausencias.detalle.justificada")
                          : t("ausencias.detalle.injustificada")}
                      </TableCell>
                      <TableCell>{d.motivo || t("ausencias.detalle.vacioMotivo")}</TableCell>
                      <TableCell>
                        {d.parcial ? t("ausencias.detalle.parcial") : t("ausencias.detalle.total")}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
}

/* ═════════════════════════════════════════════════════
   COMPONENTE principal
   ═════════════════════════════════════════════════════ */
export default function AusenciasPage() {
  const { t } = useTranslation<"translation">();

  /* rango inicial = semana actual */
  const monday = dayjs().startOf("isoWeek");
  const sunday = monday.add(6, "day");
  const [desde, setDesde] = useState(monday.format("YYYY-MM-DD"));
  const [hasta, setHasta] = useState(sunday.format("YYYY-MM-DD"));

  /* pestañas */
  const [tab, setTab] = useState(0);

  /* reportes */
  const { run: runTot, useStatus: useTotStatus } = useAusenciasTotalesReport();
  const { run: runProf, useStatus: useProfStatus } = useAusenciasProfesorReport();

  const [jobTot, setJobTot] = useState<string>();
  const [jobProf, setJobProf] = useState<string>();

  const statusTot = useTotStatus(jobTot).data;
  const statusProf = useProfStatus(jobProf).data;

  const generar = async () => {
    if (tab === 0) {
      const { jobId } = await runTot({ desde, hasta });
      setJobTot(jobId);
    } else {
      const { jobId } = await runProf({ desde, hasta });
      setJobProf(jobId);
    }
  };

  /* diálogo de ausencia (justificante) */
  const [dlgAbs, setDlgAbs] = useState<{
    open: boolean;
    data: Ausencia | null;
  }>({
    open: false,
    data: null,
  });

  const openAbsence = async (id: number) => {
    try {
      const { data } = await api.get<Ausencia>(`/ausencias/${id}`);
      setDlgAbs({ open: true, data });
    } catch (e) {
      // Error cargando ausencia
    }
  };

  const closeAbsDlg = () => setDlgAbs({ open: false, data: null });

  const handleDownloadTot = async () => {
    if (statusTot?.result?.url) {
      try {
        await downloadReportFile(statusTot.result.url);
      } catch (error) {
        console.error("Error al descargar:", error);
      }
    }
  };

  const handleDownloadProf = async () => {
    if (statusProf?.result?.url) {
      try {
        await downloadReportFile(statusProf.result.url);
      } catch (error) {
        console.error("Error al descargar:", error);
      }
    }
  };

  /* datos gráfico / tabla */
  const rowsTot = statusTot?.result?.rows ?? [];
  const rowsProf = statusProf?.result?.rows ?? [];

  return (
    <>
      <Stack spacing={3}>
        <Typography variant="h5">{t("ausencias.titulo")}</Typography>

        {/* filtros */}
        <Stack direction="row" spacing={2} alignItems="center">
          <TextField
            type="date"
            label={t("ausencias.filtros.desde")}
            value={desde}
            onChange={(e) => setDesde(e.target.value)}
          />
          <TextField
            type="date"
            label={t("ausencias.filtros.hasta")}
            value={hasta}
            onChange={(e) => setHasta(e.target.value)}
          />
          <Button variant="contained" onClick={generar}>
            {t("ausencias.filtros.generar")}
          </Button>
        </Stack>

        {/* tabs */}
        <Tabs value={tab} onChange={(_, v) => setTab(v)}>
          <Tab label={t("ausencias.tabs.totales")} />
          <Tab label={t("ausencias.tabs.porProfesor")} />
        </Tabs>

        {/* TAB 0 – totales */}
        {tab === 0 && (
          <>
            {statusTot && !statusTot.done && (
              <LinearProgress variant="determinate" value={statusTot.progress} />
            )}

            {statusTot?.done && (
              <>
                <Alert severity="success">
                  {t("ausencias.estado.exito")}{" "}
                  <Button
                    variant="text"
                    size="small"
                    onClick={handleDownloadTot}
                    sx={{ textTransform: "none", textDecoration: "underline", p: 0, minWidth: "auto" }}
                  >
                    {t("ausencias.estado.descargar")}
                  </Button>
                </Alert>

                <Paper sx={{ p: 2 }}>
                  <ResponsiveContainer width="100%" height={320}>
                    <BarChart data={rowsTot}>
                      <XAxis dataKey="tipo" />
                      <YAxis allowDecimals={false} />
                      <Tooltip />
                      <Legend />
                      <Bar dataKey="n" name="Ausencias">
                        {rowsTot.map((_, i) => (
                          <Cell key={i} fill={COLORS[i % COLORS.length]} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </Paper>
              </>
            )}
          </>
        )}

        {/* TAB 1 – por profesor */}
        {tab === 1 && (
          <>
            {statusProf && !statusProf.done && (
              <LinearProgress variant="determinate" value={statusProf.progress} />
            )}

            {statusProf?.done && (
              <>
                <Alert severity="success">
                  {t("ausencias.estado.exito")}{" "}
                  <Button
                    variant="text"
                    size="small"
                    onClick={handleDownloadProf}
                    sx={{ textTransform: "none", textDecoration: "underline", p: 0, minWidth: "auto" }}
                  >
                    {t("ausencias.estado.descargar")}
                  </Button>
                </Alert>

                <Paper sx={{ overflowX: "auto" }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell />
                        <TableCell>{t("ausencias.tabla.profesor")}</TableCell>
                        <TableCell align="right">{t("ausencias.tabla.justificadas")}</TableCell>
                        <TableCell align="right">{t("ausencias.tabla.injustificadas")}</TableCell>
                        <TableCell align="right">{t("ausencias.tabla.total")}</TableCell>
                        <TableCell>{t("ausencias.tabla.ultima")}</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {rowsProf.map((r) => (
                        <Row key={r.profesorId} row={r} onOpenAbs={openAbsence} />
                      ))}
                    </TableBody>
                  </Table>
                </Paper>
              </>
            )}
          </>
        )}
      </Stack>

      {/* diálogo de ausencia (adjuntos) */}
      <DlgAusenciaEdit
        open={dlgAbs.open}
        absence={dlgAbs.data}
        onClose={closeAbsDlg}
        onSaved={closeAbsDlg}
        canApprove={false /* en informes sólo lectura */}
      />
    </>
  );
}
